/*
 * Pure fish-contact helpers. No Preact, no stores, no fetch — everything here is
 * a plain function so it can be unit tested directly, matching lib/fishCatch.js.
 *
 * A "contact" is a fish that took, followed, was lost or was missed but never
 * landed. It shares every vocabulary with a catch except the outcome: no
 * length/weight/released, but a contact type, a count and estimated size.
 *
 * Module-level constants below are shared for the whole session — never mutate
 * them; always return new arrays.
 */

import { FISH_SPECIES, CATCH_METHODS, WATER_TYPES } from './fishCatch';

// Re-exported so fields-other.js can pull every fishContact vocabulary from one
// module instead of importing half from fishCatch.js.
export { FISH_SPECIES, CATCH_METHODS, WATER_TYPES };

// [value, label] pairs — the datalist format used throughout fields.js
// (input.js only reads datalist as el[0]/el[1] pairs, see components/form/input.js).
export const CONTACT_TYPES = [
    ['take', 'Napp'],
    ['follow', 'Følge'],
    ['lost', 'Mistet fisk'],
    ['miss', 'Bom'],
];

export function contactTypeLabel(value) {
    if (!value) {
        return '';
    }
    const match = CONTACT_TYPES.find(([contactType]) => contactType === value);
    return match ? match[1] : '';
}

// ' · '-joined headline, e.g. '3 × Napp · Laks · ~80 cm'. The '~' marks the
// measurement as an estimate without hardcoding a translatable word here — the
// Norwegian labels live in CONTACT_TYPES, exactly as formatCatchHeadline keeps
// itself language-neutral while FISH_SPECIES carries the labels.
// Every numeric guard is an explicit `> 0` comparison: a truthy check would let
// a 0 through into the JSX that renders this string.
export function formatContactHeadline(fishContact) {
    if (!fishContact) {
        return '';
    }
    const parts = [];
    const label = contactTypeLabel(fishContact.contactType);
    if (label) {
        parts.push(fishContact.contactCount > 1 ? `${fishContact.contactCount} × ${label}` : label);
    }
    const species = String(fishContact.species || '').trim();
    if (species) {
        parts.push(species.charAt(0).toUpperCase() + species.slice(1));
    }
    if (fishContact.estimatedLengthCm > 0) {
        parts.push(`~${fishContact.estimatedLengthCm} cm`);
    }
    if (fishContact.estimatedWeightKg > 0) {
        parts.push(`~${fishContact.estimatedWeightKg} kg`);
    }
    return parts.join(' · ');
}
